import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { RateHistorySkeleton } from './RateHistorySkeleton';
import { RateHistoryView } from './RateHistoryView';
import { useRateHistory } from './useRateHistory';

/**
 * The rate change history — a self-served widget.
 *
 * Owns its data hook and its `ErrorBoundary`, so a history failure cannot take
 * the settings form down with it: the rates stay editable even if the audit
 * list cannot be loaded. The throw happens in the inner child, since React does
 * not catch a component's own throws.
 */
export const RateHistory: React.FC = () => {
    const history = useRateHistory();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('settings.history.error.title'),
            errorMessage: t('settings.history.error.message'),
            errorRetry: t('settings.error.retry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="settings-history-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="settings-history-error-boundary"
            onRetry={history.onRetry}
            resetKeys={[history.error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <RateHistoryContent history={history} />
        </ErrorBoundary>
    );
};

type RateHistoryContentProps = {
    history: ReturnType<typeof useRateHistory>;
};

const RateHistoryContent: React.FC<RateHistoryContentProps> = ({ history }) => {
    if (history.error) {
        throw history.error;
    }
    if (history.isLoading) {
        return <RateHistorySkeleton />;
    }
    return <RateHistoryView history={history} />;
};
